import equal from 'fast-deep-equal'
import { DateTime } from "luxon";
import { computed, ref } from "vue";

const distinct = (value, index, self) => self.indexOf(value) === index;

const poll_api_call = async function (fnApiCall, fnPollCondition, delay_in_milliseconds, timeout_in_milliseconds = 60000) {
    const endTime = Number(new Date()) + timeout_in_milliseconds;
    let result = await fnApiCall();
    while (fnPollCondition(result)) {
        if (Number(new Date()) < endTime) {
            await delay(delay_in_milliseconds);
            result = await fnApiCall();
        }
        else {
            result = { is_timeout: true };
            break;
        }
    }
    return result;
};

const delay = function (milliseconds = 500) {
    return new Promise((resolve) => {
        setTimeout(resolve, milliseconds);
    });
};

const mmddyyyy_to_yyyymmdd = function (value) {
    if (value) {
        const split = value.split('/');
        return `${ split[2] }-${ split[0] }-${ split[1] }`;
    }
    else {
        return value;
    }
}

const yyyymmdd_to_mmddyyyy = function (value) {
    if (value) {
        const split = value.split('-');
        return `${ split[1] }-${ split[2] }-${ split[0] }`;
    }
    else {
        return value;
    }
}

const home_type = function (usage) {
    switch (usage) {
        case 500:
            return 'Apartment'
        case 1000:
            return 'Small Home'
        case 1500:
            return 'Medium Home'
        case 2000:
            return 'Large Home'
        default:
            return 'Your Home'
    }
}

const plan_name = function (plan) {
    return plan?.product?.display_name || plan?.product?.name || 'this plan';
}

const plan_brand = function (plan) {
    return plan?.product?.brand?.name || 'Provider';
}

const plan_brand_image_name = function (plan) {
    return plan_brand(plan)?.replace(/ /gm, '_').toLowerCase();
}

const plan_brand_legal_name = function (plan) {
    return plan?.product?.brand?.legal_name || plan?.product?.brand?.name || 'Provider';
}

const plan_brand_address = function (plan) {
    return plan?.product?.brand?.contact_info?.support?.address;
}

const plan_brand_support_email = function (plan) {
    return plan?.product?.brand?.contact_info?.support?.email;
}

const plan_brand_website = function (plan) {
    return plan?.product?.brand?.contact_info?.support?.url;
}

const plan_credit_check = function (plan) {
    return plan?.product?.brand?.configuration?.has_credit_check_method;
}

const plan_brand_puct = function (plan) {
    return plan?.product?.brand?.puct_number;
}

const plan_phone_number = function (plan) {
    if (plan.product.phone_number) {
        return plan.product.phone_number;
    }
    else {
        return plan.product.brand.contact_info?.sales?.phone_number;
    }
}

const plan_brand_phone_number = function (plan) {
    if (plan?.product?.brand?.contact_info?.brand?.phone_number) {
        return plan?.product?.brand?.contact_info?.brand?.phone_number
    }
    else if (plan.product.phone_number) {
        return plan.product.phone_number;
    }
    else {
        return plan.product.brand.contact_info?.sales?.phone_number;
    }
}

const plan_support_phone_number = function (plan) {
    return plan?.product?.brand?.contact_info?.support?.phone_number;
}

const plan_allow_drivers_license = function (plan) {
    return (plan?.product?.brand?.configuration?.allow_drivers_license || !plan?.product?.brand?.configuration?.social_security_number_required);
}

const plan_allow_current_address = function (plan) {
    return plan?.product?.brand?.configuration?.allow_current_address;
}

const plan_doc_link = function (plan, type) {
    return plan?.document_links?.find(x => x.type === type)?.link
}

const get_deposit_payment_link = function (order) {
    const url = order.order_submit_response?.value?.payment_requirements?.url;
    if (order.brand === 'Payless Power') {
        return `${ url }&redirect=${ location.origin }/order/payment_complete?order_id=${ order._id }`
    }
    else {
        return url;
    }
}

const get_short_day = function (day) {
    switch (day) {
        case 'monday':
            return 'Mon';
        case 'tuesday':
            return 'Tue';
        case 'wednesday':
            return 'Wed';
        case 'thursday':
            return 'Thu';
        case 'friday':
            return 'Fri';
        case 'saturday':
            return 'Sat';
        case 'sunday':
            return 'Sun';
    }
}

const get_min = function (min) {
    if (min) {
        return `:${ min }`
    }
    else {
        return ''
    }
}

const get_time = function (hour, min) {
    if (hour === 0) {
        return `12${ get_min(min) } am`
    }
    else if (hour === 12) {
        return `12${ get_min(min) } pm`
    }
    else {
        const twelve_hour = hour % 12;
        const mod = hour < 12 ? 'am' : 'pm';
        return `${ twelve_hour }${ get_min(min) } ${ mod }`
    }
}

const plan_hours = function (plan) {
    if (plan?.product?.brand?.operation_hours) {
        const hours = [];
        for (const [key, value] of Object.entries(plan.product.brand.operation_hours)) {
            const found = hours.find(x => equal(x.hours, value));
            if (found) {
                found.days.push(key)
            }
            else {
                hours.push({
                    hours: value,
                    days: [key]
                })
            }
        }
        return hours;
    }
}

const plan_hours_string = function (plan) {
    const current_plan_hours = plan_hours(plan);
    if (current_plan_hours) {
        const result = current_plan_hours.map(x => {
            let days = get_short_day(x.days[0]);
            if (x.days.length > 1) {
                days += `-${ get_short_day(x.days[x.days.length - 1]) }`
            }
            return `${ days } ${ get_time(x.hours.start, x.hours.start_min) } to ${ get_time(x.hours.end, x.hours.end_min) } CST`
        }).join(', ')
        return `(${ result })`
    }
    return '(24 Hours, 7 days a week)'
}

const states = {
    "AL": "Alabama",
    "AK": "Alaska",
    "AS": "American Samoa",
    "AZ": "Arizona",
    "AR": "Arkansas",
    "CA": "California",
    "CO": "Colorado",
    "CT": "Connecticut",
    "DE": "Delaware",
    "DC": "District Of Columbia",
    "FM": "Federated States Of Micronesia",
    "FL": "Florida",
    "GA": "Georgia",
    "GU": "Guam",
    "HI": "Hawaii",
    "ID": "Idaho",
    "IL": "Illinois",
    "IN": "Indiana",
    "IA": "Iowa",
    "KS": "Kansas",
    "KY": "Kentucky",
    "LA": "Louisiana",
    "ME": "Maine",
    "MH": "Marshall Islands",
    "MD": "Maryland",
    "MA": "Massachusetts",
    "MI": "Michigan",
    "MN": "Minnesota",
    "MS": "Mississippi",
    "MO": "Missouri",
    "MT": "Montana",
    "NE": "Nebraska",
    "NV": "Nevada",
    "NH": "New Hampshire",
    "NJ": "New Jersey",
    "NM": "New Mexico",
    "NY": "New York",
    "NC": "North Carolina",
    "ND": "North Dakota",
    "MP": "Northern Mariana Islands",
    "OH": "Ohio",
    "OK": "Oklahoma",
    "OR": "Oregon",
    "PW": "Palau",
    "PA": "Pennsylvania",
    "PR": "Puerto Rico",
    "RI": "Rhode Island",
    "SC": "South Carolina",
    "SD": "South Dakota",
    "TN": "Tennessee",
    "TX": "Texas",
    "UT": "Utah",
    "VT": "Vermont",
    "VI": "Virgin Islands",
    "VA": "Virginia",
    "WA": "Washington",
    "WV": "West Virginia",
    "WI": "Wisconsin",
    "WY": "Wyoming"
};


const state_list = Object.entries(states).map(([key, value]) => ({ value: key, label: value }));

const format_dollar_amount = function (amount) {
    if (!isNaN(amount)) {
        return amount >= 0 ? amount.toFixed(2) : `(${ (amount * -1).toFixed(2) })`
    }
    return amount;
}

const format_date = (date, hide_time) => {
    try {
        if (date) {
            const datetime = DateTime.fromISO(date, { zone: 'America/Chicago' })
            if (hide_time) {
                return datetime.toFormat(`EEEE MMM dd',' yyyy`, {})
            }
            return datetime.toFormat(`EEEE MMM dd',' yyyy',' h:mm:ss a ZZZZ`)
        }
    }
    catch (err) {
        return date;
    }
}

const get_start_date_string = (action, date) => {
    if (action === 'ASAP') {
        return 'Standard Switch (1-7 Days)'
    }
    else {
        return `${ action } on ${ format_date(date, true) }`
    }
}

const brand_to_provider = function (brand) {
    switch (brand) {
        case '4Change Energy':
        case 'TXU Energy':
        case 'TriEagle Energy':
        case 'Veteran Energy':
        case 'Express Energy':
        case 'Ambit Energy':
            return 'Vistra'
        case 'Just Energy':
        case 'Amigo Energy':
        case 'Tara Energy':
            return 'Fulcrum';
        case 'Constellation':
            return 'Constellation Energy'
        case 'Gexa Energy':
        case 'Frontier Utilities':
            return 'NextEra'
        case 'Pulse Power':
        case 'New Power Texas':
            return 'Shell'
        case 'Payless Power':
            return 'Young Energy'
        case 'Reliant':
        case 'Green Mountain':
        case 'Cirro Energy':
        case 'Discount Power':
        case 'Direct Energy':
            return 'NRG'
        case 'Flagship Power':
        case 'JUICE':
            return 'Flagship'
        default:
            return 'Unknown'
    }
}

const plan_to_ecommerce = function ({ plan, sort_index, list_id }) {
    return {
        item_id: plan?.product._id,
        item_name: plan?.product.name,
        affiliation: brand_to_provider(plan?.product?.brand?.name),
        // coupon: '',
        // discount: 2.22,
        index: sort_index,
        item_brand: plan?.product.brand?.name,
        item_category: 'Electricity',
        item_category2: 'Residential',
        item_category3: 'Texas',
        item_category4: plan?.tdsp?.short_name,
        // item_category5: "Short sleeve",
        ...list_id && { item_list_id: list_id },
        ...list_id && { item_list_name: list_id },
        item_variant: `${ plan?.product?.term } months`,
        // location_id: "ChIJIQBpAG2ahYAR_6128GcTUEo",
        // price: 9.99,
        quantity: 1,

        plan_id: plan?._id,
        price_500: plan?.display_pricing_500?.avg,
        price_1000: plan?.display_pricing_1000?.avg,
        price_2000: plan?.display_pricing_2000?.avg,
    }
}

const isFunction = function (functionToCheck) {
    return functionToCheck && {}.toString.call(functionToCheck) === '[object Function]';
}

const debounce = function (func, wait) {
    var timeout;
    var waitFunc;

    return function () {
        if (isFunction(wait)) {
            waitFunc = wait;
        }
        else {
            waitFunc = function () {
                return wait
            };
        }

        var context = this, args = arguments;
        var later = function () {
            timeout = null;
            func.apply(context, args);
        };
        clearTimeout(timeout);
        timeout = setTimeout(later, waitFunc());
    };
};

const loaded = ref(false);

export default () => {
    return {
        loaded,
        debounce,
        mmddyyyy_to_yyyymmdd,
        yyyymmdd_to_mmddyyyy,
        distinct,
        home_type,
        plan_name,
        plan_brand,
        plan_brand_image_name,
        plan_doc_link,
        plan_brand_puct,
        plan_brand_legal_name,
        plan_brand_address,
        plan_brand_support_email,
        plan_brand_website,
        plan_brand_phone_number,
        plan_phone_number,
        plan_support_phone_number,
        plan_hours_string,
        plan_credit_check,
        plan_allow_drivers_license,
        plan_allow_current_address,
        plan_to_ecommerce,
        brand_to_provider,
        states,
        state_list,
        poll_api_call,
        get_deposit_payment_link,
        format_dollar_amount,
        delay,
        format_date,
        get_start_date_string
    }
}
